import React, { useState } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Contained banner animations with image transitions
const containedBannerStyles = `
  .contained-banner-slide {
    transition: all 1.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: scale(1) rotate(0deg);
  }
  
  /* Image transition animations */
  .banner-image-transition {
    transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .banner-image-fade-out {
    opacity: 0;
    transform: scale(1.1);
  }
  .banner-image-fade-in {
    opacity: 1;
    transform: scale(1);
    animation: imageSlideIn 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  @keyframes imageSlideIn {
    0% {
      opacity: 0;
      transform: scale(0.9) translateX(30px);
    }
    50% {
      opacity: 0.7;
      transform: scale(0.95) translateX(10px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateX(0);
    }
  }
  
  .contained-banner-content {
    animation: contentFadeInUp 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  @keyframes contentFadeInUp {
    0% {
      opacity: 0;
      transform: translateY(25px);
    }
    60% {
      opacity: 0.8;
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .contained-banner-button {
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .contained-banner-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

// Inject styles
if (
  typeof document !== "undefined" &&
  !document.querySelector("#contained-banner-animations")
) {
  const style = document.createElement("style");
  style.id = "contained-banner-animations";
  style.textContent = containedBannerStyles;
  document.head.appendChild(style);
}

export default function ContainedBanner({ banners, handleCTANavigation }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Sort banners by Banner_Position for proper slider order
  const sortedBanners = banners.sort(
    (a, b) => (a.Banner_Position || 0) - (b.Banner_Position || 0)
  );

  // Auto-rotate through banners if multiple
  React.useEffect(() => {
    if (sortedBanners.length <= 1) return;

    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentIndex(
            (prevIndex) => (prevIndex + 1) % sortedBanners.length
          );
          setTimeout(() => setIsTransitioning(false), 100);
        }, 200);
      }
    }, 8000); // Change every 8 seconds

    return () => clearInterval(interval);
  }, [sortedBanners.length, isTransitioning]);

  const handlePrevious = () => {
    if (isTransitioning) return; // Prevent rapid clicking
    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? sortedBanners.length - 1 : prevIndex - 1
      );
      setTimeout(() => setIsTransitioning(false), 100);
    }, 200);
  };

  const handleNext = () => {
    if (isTransitioning) return; // Prevent rapid clicking
    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % sortedBanners.length);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 200);
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  const currentBanner = sortedBanners[currentIndex];

  return (
    <Container maxWidth="lg" sx={{ mt: 12, mb: 2 }}>
      <Box
        className="contained-banner-card"
        sx={{
          position: "relative",
          width: "100%",
          height: {
            xs: "300px",
            sm: "350px",
            md: "400px",
            lg: "450px",
          },
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
          cursor: "pointer",
          transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          },
        }}
      >
        {/* Background Image with Crossfade Effect */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
          }}
        >
          <Box
            component="img"
            key={`banner-${currentIndex}`}
            src={process.env.BASEURL + currentBanner.Banner_Image_File_Path}
            alt={currentBanner.Image_Heading_Text || "Banner"}
            className={`contained-banner-slide banner-image-transition ${
              isTransitioning ? "banner-image-fade-out" : "banner-image-fade-in"
            }`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(1.05) contrast(1.05) saturate(1.1)",
              opacity: isTransitioning ? 0 : 1,
              transition:
                "opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              transform: isTransitioning ? "scale(1.1)" : "scale(1)",
            }}
          />
        </Box>
        {/* Navigation Arrows */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: 20,
            transform: "translateY(-50%)",
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(255, 255, 255, 0.9)",
              borderRadius: 1,
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.2)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              backdropFilter: "blur(8px)",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.25)"
                    : "rgba(255, 255, 255, 1)",
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
            onClick={handlePrevious}
          >
            <ArrowForwardIcon
              sx={{
                fontSize: 24,
                transform: "rotate(-90deg)",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#ffffff" : "#333333",
              }}
            />
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(255, 255, 255, 0.9)",
              borderRadius: 1,
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.2)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              backdropFilter: "blur(8px)",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.25)"
                    : "rgba(255, 255, 255, 1)",
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
            onClick={handleNext}
          >
            <ArrowForwardIcon
              sx={{
                fontSize: 24,
                transform: "rotate(90deg)",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#ffffff" : "#333333",
              }}
            />
          </Box>
        </Box>
        {/* Content Container - Left Bottom Aligned */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            px: { xs: 3, sm: 4, md: 6 },
            pb: { xs: 3, sm: 4, md: 6 },
            width: "70%",
          }}
        >
          <Box
            key={`content-${currentIndex}`}
            sx={{
              maxWidth: "500px",
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? "translateY(20px)" : "translateY(0)",
              transition: "all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            className="contained-banner-content"
          >
            {/* Main Heading */}
            {currentBanner.Image_Heading_Text && (
              <Typography
                variant="h2"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: {
                    xs: "1.8rem",
                    sm: "2.2rem",
                    md: "2.8rem",
                    lg: "3.2rem",
                  },
                  lineHeight: 1.2,
                  mb: 2,
                  letterSpacing: "-0.02em",
                  textShadow: "0 4px 12px rgba(0,0,0,0.6)",
                }}
              >
                {currentBanner.Image_Heading_Text}
              </Typography>
            )}

            {/* Sub Heading */}
            {currentBanner.Image_Sub_Heading_Text && (
              <Typography
                variant="h5"
                sx={{
                  color: "#ffffff",
                  fontWeight: 400,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.1rem",
                    md: "1.25rem",
                  },
                  lineHeight: 1.4,
                  mb: 3,
                  opacity: 0.95,
                  textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}
              >
                {currentBanner.Image_Sub_Heading_Text}
              </Typography>
            )}

            {/* Description Text */}
            {currentBanner.Image_Text && (
              <Typography
                variant="body1"
                sx={{
                  color: "#ffffff",
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                  },
                  lineHeight: 1.6,
                  mb: 3,
                  maxWidth: "400px",
                  opacity: 0.9,
                  textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}
              >
                {currentBanner.Image_Text}
              </Typography>
            )}

            {/* CTA Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              {/* Primary CTA */}
              {currentBanner.Banner_CTA_Text &&
                currentBanner.Banner_CTA_Button_Link && (
                  <Button
                    variant="contained"
                    size="medium"
                    className="contained-banner-button"
                    onClick={() =>
                      handleCTANavigation(currentBanner.Banner_CTA_Button_Link)
                    }
                    sx={{
                      backgroundColor: "#000000",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "1rem",
                      px: 3,
                      py: 1.2,
                      borderRadius: 1,
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      "&:hover": {
                        backgroundColor: "#333333",
                      },
                    }}
                  >
                    {currentBanner.Banner_CTA_Text}
                  </Button>
                )}

              {/* Secondary CTA */}
              {currentBanner.Banner_Link && (
                <Button
                  variant="text"
                  size="medium"
                  onClick={() => handleCTANavigation(currentBanner.Banner_Link)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    color: "#ffffff",
                    fontWeight: 500,
                    fontSize: "1rem",
                    px: 2,
                    py: 1.2,
                    textTransform: "none",
                    textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateX(4px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Learn More
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {/* Slide indicators */}
        <Box
          sx={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            display: "flex",
            gap: 1,
          }}
        >
          {sortedBanners.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex ? "#fff" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#fff",
                },
              }}
              onClick={() => {
                if (index !== currentIndex && !isTransitioning) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(index);
                    setTimeout(() => setIsTransitioning(false), 100);
                  }, 200);
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Container>
  );
}
